'use client';

import React, { useEffect, useRef, useCallback, useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/contexts/DarkModeContext';

export interface WaveformTraceData {
  /** Unique ID for this trace */
  id: string;
  /** Display name */
  name: string;
  /** Data points (values) */
  data: number[];
  /** Color for this waveform */
  color: string;
  /** Sample rate in Hz */
  sampleRate?: number;
  /** Whether this trace is visible */
  visible?: boolean;
  /** Opacity (0-1) */
  opacity?: number;
  /** Type of recording (acceleration or velocity) */
  type?: 'acceleration' | 'velocity';
}

export interface LoopRegion {
  start: number;
  end: number;
}

export type InteractionMode = 'handlebars' | 'zoom';

export interface WaveformControls {
  play: () => void;
  pause: () => void;
  stop: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  seekTo: (timeInSeconds: number) => void;
  isReady: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  zoomLevel: number;
  formatTime: (seconds: number) => string;
  // Loop region controls
  loopRegion: LoopRegion | null;
  isLooping: boolean;
  setLoopRegion: (region: LoopRegion | null) => void;
  toggleLoop: () => void;
  clearLoopRegion: () => void;
  // Interaction mode (handlebars vs zoom)
  interactionMode: InteractionMode;
  setInteractionMode: (mode: InteractionMode) => void;
  toggleInteractionMode: () => void;
}

export interface WaveformMarker {
  id: string;
  /** Time position in seconds */
  time: number;
  /** Label to display */
  label?: string;
  /** Color of the marker line */
  color?: string;
  /** Whether this is a harmonic/sideband marker */
  isHarmonic?: boolean;
  /** Group ID for harmonics */
  groupId?: string;
  /** Whether the line should be dashed */
  isDashed?: boolean;
}

export interface MultiWaveSurferChartProps {
  /** Array of waveform traces to display (can have both acceleration and velocity) */
  traces: WaveformTraceData[];
  /** Height of the chart */
  height?: number;
  /** Container class name */
  className?: string;
  /** Enable zoom via scroll wheel */
  enableZoom?: boolean;
  /** Minimum zoom level (pixels per second) */
  minPxPerSec?: number;
  /** Callback when playback position changes */
  onTimeUpdate?: (currentTime: number) => void;
  /** Cursor color */
  cursorColor?: string;
  /** All available traces for external use */
  allTraces?: WaveformTraceData[];
  /** Callback when a trace is selected */
  onTraceSelect?: (traceId: string) => void;
  /** Callback when controls are ready (called once when ready, and on state changes) */
  onControlsReady?: (controls: WaveformControls) => void;
  /** Callback for playback state changes (more frequent, for time updates) */
  onPlaybackStateChange?: (state: { isPlaying: boolean; currentTime: number; duration: number }) => void;
  /** Vertical line markers */
  markers?: WaveformMarker[];
  /** Whether marker selection mode is active */
  isMarkerSelectionMode?: boolean;
  /** Callback when a marker is added */
  onAddMarker?: (time: number) => void;
  /** Callback when a marker is clicked */
  onMarkerClick?: (markerId: string, position: { x: number; y: number }) => void;
  /** Callback when a marker is removed */
  onRemoveMarker?: (markerId: string) => void;
  /** Selected marker ID */
  selectedMarkerId?: string | null;
  /** Whether harmonic selection mode is active (two-click workflow) */
  isHarmonicSelectionMode?: boolean;
  /** Whether sideband selection mode is active (two-click workflow) */
  isSideBandsSelectionMode?: boolean;
  /** Callback when a click happens in harmonic/sideband mode */
  onHarmonicClick?: (time: number) => void;
  /** Pending harmonic creation state (waiting for second click) */
  pendingHarmonicCreation?: {
    centerPoint: number;
    type: 'harmonic' | 'sideBands';
    labelPrefix: string;
    color: string;
    groupId: string;
  } | null;
  /** Timestamp requested by user (what they clicked on) */
  requestedTimestamp?: string;
  /** Actual recording timestamp returned by backend (may differ from requested) */
  actualTimestamp?: string;
}

/**
 * MultiWaveSurferChart - Time Waveform player
 * Displays both acceleration and velocity waveforms OVERLAID on the same canvas
 * The active mode shows at full opacity, inactive at reduced opacity
 */
export const MultiWaveSurferChart = forwardRef<WaveformControls, MultiWaveSurferChartProps>(({
  traces,
  height = 320,
  className,
  enableZoom = true,
  minPxPerSec = 100,
  onTimeUpdate,
  cursorColor,
  onControlsReady,
  onPlaybackStateChange,
  markers = [],
  isMarkerSelectionMode = false,
  onAddMarker,
  onMarkerClick,
  onRemoveMarker,
  selectedMarkerId,
  isHarmonicSelectionMode = false,
  isSideBandsSelectionMode = false,
  onHarmonicClick,
  pendingHarmonicCreation,
  requestedTimestamp,
  actualTimestamp,
}, ref) => {
  const darkMode = useDarkMode();
  const isDark = (darkMode as any).isDark || false;

  // Single container for overlaid waveforms
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const accelerationContainerRef = useRef<HTMLDivElement>(null);
  const velocityContainerRef = useRef<HTMLDivElement>(null);
  const accelerationWsRef = useRef<WaveSurfer | null>(null);
  const velocityWsRef = useRef<WaveSurfer | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(minPxPerSec);
  const [autoFitZoom, setAutoFitZoom] = useState<number | null>(null);

  // Loop region state
  const [loopRegion, setLoopRegionState] = useState<LoopRegion | null>(null);
  const [isLooping, setIsLooping] = useState(false);
  const [isDraggingHandle, setIsDraggingHandle] = useState<'start' | 'end' | null>(null);
  const [isCreatingRegion, setIsCreatingRegion] = useState(false);
  const [regionStartTime, setRegionStartTime] = useState<number | null>(null);
  const loopCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Interaction mode: 'handlebars' (drag to select loop region) or 'zoom' (drag to zoom like Plotly)
  const [interactionMode, setInteractionMode] = useState<InteractionMode>('handlebars');
  const [zoomSelectionStart, setZoomSelectionStart] = useState<number | null>(null);
  const [zoomSelectionEnd, setZoomSelectionEnd] = useState<number | null>(null);

  // Zoom constants
  const MIN_ZOOM = 10; // Allow zooming out further for auto-fit
  const MAX_ZOOM = 1000;
  const ZOOM_STEP = 1.5;

  // Separate traces by type
  const accelerationTrace = useMemo(() => traces.find(t => t.type === 'acceleration'), [traces]);
  const velocityTrace = useMemo(() => traces.find(t => t.type === 'velocity'), [traces]);

  // Track opacity changes separately
  const accelerationOpacity = accelerationTrace?.opacity ?? 1;
  const velocityOpacity = velocityTrace?.opacity ?? 1;

  // Calculate auto-fit zoom level (fits entire waveform in container) - like Plotly behavior
  const calculateAutoFitZoom = useCallback(() => {
    if (!chartContainerRef.current) return minPxPerSec;
    const containerWidth = chartContainerRef.current.clientWidth;
    // Get duration from data
    const trace = accelerationTrace || velocityTrace;
    const sampleRate = trace?.sampleRate || 4000;
    const dataLength = trace?.data?.length || 0;
    const estimatedDuration = dataLength > 0 ? dataLength / sampleRate : duration || 5;

    // Calculate pixels per second to fit the entire waveform in the container
    // Subtract y-axis width (48px) from container width
    const availableWidth = containerWidth;
    const fitZoom = availableWidth / estimatedDuration;

    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));
  }, [accelerationTrace, velocityTrace, duration, minPxPerSec]);

  // Determine active trace (the one with full opacity)
  const activeTrace = useMemo(() => {
    return accelerationOpacity >= velocityOpacity ? accelerationTrace : velocityTrace;
  }, [accelerationTrace, velocityTrace, accelerationOpacity, velocityOpacity]);

  // Default colors - matching PlotlyChart colors
  const colors = useMemo(() => ({
    cursor: cursorColor || '#ef4444',
    background: isDark ? '#2d3748' : 'white',  // Matches PlotlyChart plot_bgcolor
  }), [cursorColor, isDark]);

  // Format timestamp for display (shows time portion)
  const formatTimestamp = useCallback((timestamp: string | undefined) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return timestamp;
    }
  }, []);

  // Check if timestamps differ (rounded to nearest 30 minutes vs exact)
  const timestampInfo = useMemo(() => {
    if (!actualTimestamp) return null;
    const formattedActual = formatTimestamp(actualTimestamp);
    const formattedRequested = formatTimestamp(requestedTimestamp);
    const isDifferent = requestedTimestamp && actualTimestamp !== requestedTimestamp;
    return { formattedActual, formattedRequested, isDifferent };
  }, [actualTimestamp, requestedTimestamp, formatTimestamp]);

  // Format time as MM:SS.ms
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  }, []);

  // Height for the waveform area (excluding x-axis and legend)
  const waveformHeight = height - 64; // 32px for x-axis, 32px for legend

  // Control functions - play the active trace and stop the inactive one
  const play = useCallback(() => {
    const isAccelerationActive = accelerationOpacity >= velocityOpacity;
    const activeWs = isAccelerationActive
      ? accelerationWsRef.current
      : velocityWsRef.current;
    const inactiveWs = isAccelerationActive
      ? velocityWsRef.current
      : accelerationWsRef.current;

    // Stop the inactive waveform first
    if (inactiveWs) {
      inactiveWs.pause();
      inactiveWs.setTime(0);
    }

    // Play the active waveform
    if (activeWs && isReady) {
      // If a region is selected, always start from region start
      if (loopRegion) {
        activeWs.setTime(loopRegion.start);
      }
      activeWs.play();
    }
  }, [isReady, accelerationOpacity, velocityOpacity, loopRegion]);

  const pause = useCallback(() => {
    accelerationWsRef.current?.pause();
    velocityWsRef.current?.pause();
  }, []);

  const stop = useCallback(() => {
    accelerationWsRef.current?.stop();
    velocityWsRef.current?.stop();
    setCurrentTime(0);
  }, []);

  // Seek to a specific time in seconds
  const seekTo = useCallback((timeInSeconds: number) => {
    if (!isReady || duration === 0) return;
    // Clamp time to valid range
    const clampedTime = Math.max(0, Math.min(timeInSeconds, duration));
    // WaveSurfer's seekTo takes a percentage (0-1)
    const seekPercentage = clampedTime / duration;
    accelerationWsRef.current?.seekTo(seekPercentage);
    velocityWsRef.current?.seekTo(seekPercentage);
    setCurrentTime(clampedTime);
  }, [isReady, duration]);

  // Zoom functions
  const zoomIn = useCallback(() => {
    const newZoom = Math.min(zoomLevel * ZOOM_STEP, MAX_ZOOM);
    setZoomLevel(newZoom);
    accelerationWsRef.current?.zoom(newZoom);
    velocityWsRef.current?.zoom(newZoom);
  }, [zoomLevel]);

  const zoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel / ZOOM_STEP, MIN_ZOOM);
    setZoomLevel(newZoom);
    accelerationWsRef.current?.zoom(newZoom);
    velocityWsRef.current?.zoom(newZoom);
  }, [zoomLevel]);

  const resetZoom = useCallback(() => {
    // Reset to auto-fit zoom (fits entire waveform in container)
    const fitZoom = autoFitZoom ?? calculateAutoFitZoom();
    setZoomLevel(fitZoom);
    accelerationWsRef.current?.zoom(fitZoom);
    velocityWsRef.current?.zoom(fitZoom);
  }, [autoFitZoom, calculateAutoFitZoom]);

  // Loop region functions
  const setLoopRegion = useCallback((region: LoopRegion | null) => {
    setLoopRegionState(region);
    // If region is cleared, also disable looping
    if (!region) {
      setIsLooping(false);
    }
  }, []);

  const toggleLoop = useCallback(() => {
    if (!loopRegion) return;
    setIsLooping(prev => !prev);
  }, [loopRegion]);

  const clearLoopRegion = useCallback(() => {
    setLoopRegionState(null);
    setIsLooping(false);
  }, []);

  // Toggle between handlebars and zoom interaction modes
  const toggleInteractionMode = useCallback(() => {
    setInteractionMode(prev => prev === 'handlebars' ? 'zoom' : 'handlebars');
    // Clear any in-progress zoom selection
    setZoomSelectionStart(null);
    setZoomSelectionEnd(null);
  }, []);

  // Zoom to a specific time region (for zoom mode)
  const zoomToRegion = useCallback((start: number, end: number) => {
    if (!chartContainerRef.current || end <= start) return;

    const containerWidth = chartContainerRef.current.clientWidth;
    const regionDuration = end - start;

    // Calculate zoom to fit the selected region in the container
    const newZoom = containerWidth / regionDuration;
    const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

    setZoomLevel(clampedZoom);
    accelerationWsRef.current?.zoom(clampedZoom);
    velocityWsRef.current?.zoom(clampedZoom);

    // Scroll to the start of the selected region
    // WaveSurfer's seekTo takes a percentage (0-1)
    const seekPercentage = start / duration;
    accelerationWsRef.current?.seekTo(seekPercentage);
    velocityWsRef.current?.seekTo(seekPercentage);
  }, [duration]);

  // Helper to get time from mouse position
  const getTimeFromMouseEvent = useCallback((e: MouseEvent | React.MouseEvent) => {
    if (!chartContainerRef.current) return 0;
    const rect = chartContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const effectiveDuration = duration > 0 ? duration : 5;
    return percentage * effectiveDuration;
  }, [duration]);

  // Handle mouse move for dragging handles or creating regions
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const time = getTimeFromMouseEvent(e);

    if (isDraggingHandle && loopRegion) {
      // Dragging existing handle (only in handlebars mode)
      if (isDraggingHandle === 'start') {
        const newStart = Math.min(time, loopRegion.end - 0.1);
        setLoopRegionState({ start: Math.max(0, newStart), end: loopRegion.end });
      } else {
        const effectiveDuration = duration > 0 ? duration : 5;
        const newEnd = Math.max(time, loopRegion.start + 0.1);
        setLoopRegionState({ start: loopRegion.start, end: Math.min(effectiveDuration, newEnd) });
      }
    } else if (isCreatingRegion && regionStartTime !== null) {
      if (interactionMode === 'zoom') {
        // Zoom mode: update zoom selection
        setZoomSelectionEnd(time);
      } else {
        // Handlebars mode: create loop region
        const effectiveDuration = duration > 0 ? duration : 5;
        const start = Math.max(0, Math.min(regionStartTime, time));
        const end = Math.min(effectiveDuration, Math.max(regionStartTime, time));
        if (end - start > 0.05) { // Minimum region width
          setLoopRegionState({ start, end });
        }
      }
    }
  }, [isDraggingHandle, isCreatingRegion, regionStartTime, loopRegion, duration, getTimeFromMouseEvent, interactionMode]);

  // Handle mouse up - finish dragging
  const handleMouseUp = useCallback(() => {
    // In zoom mode, apply the zoom when mouse is released
    if (interactionMode === 'zoom' && isCreatingRegion && zoomSelectionStart !== null && zoomSelectionEnd !== null) {
      const start = Math.min(zoomSelectionStart, zoomSelectionEnd);
      const end = Math.max(zoomSelectionStart, zoomSelectionEnd);
      if (end - start > 0.05) { // Minimum selection width
        zoomToRegion(start, end);
      }
      // Clear zoom selection
      setZoomSelectionStart(null);
      setZoomSelectionEnd(null);
    }

    setIsDraggingHandle(null);
    setIsCreatingRegion(false);
    setRegionStartTime(null);
  }, [interactionMode, isCreatingRegion, zoomSelectionStart, zoomSelectionEnd, zoomToRegion]);

  // Add global mouse listeners for drag operations
  useEffect(() => {
    if (isDraggingHandle || isCreatingRegion) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingHandle, isCreatingRegion, handleMouseMove, handleMouseUp]);

  // Region playback logic - constrain playback to selected region
  // When region is selected: stop at end (or loop if enabled)
  useEffect(() => {
    if (!loopRegion || !isPlaying) {
      // Clear interval when no region or not playing
      if (loopCheckIntervalRef.current) {
        clearInterval(loopCheckIntervalRef.current);
        loopCheckIntervalRef.current = null;
      }
      return;
    }

    // Check playback position periodically
    loopCheckIntervalRef.current = setInterval(() => {
      const activeWs = accelerationOpacity >= velocityOpacity
        ? accelerationWsRef.current
        : velocityWsRef.current;

      if (activeWs) {
        const current = activeWs.getCurrentTime();
        // If past region end
        if (current >= loopRegion.end) {
          if (isLooping) {
            // Loop back to start
            activeWs.setTime(loopRegion.start);
          } else {
            // Stop playback at region end
            activeWs.pause();
            activeWs.setTime(loopRegion.start); // Reset to region start for next play
          }
        }
      }
    }, 50); // Check every 50ms for smooth playback

    return () => {
      if (loopCheckIntervalRef.current) {
        clearInterval(loopCheckIntervalRef.current);
        loopCheckIntervalRef.current = null;
      }
    };
  }, [loopRegion, isPlaying, isLooping, accelerationOpacity, velocityOpacity]);

  // Expose controls via ref
  useImperativeHandle(ref, () => ({
    play,
    pause,
    stop,
    zoomIn,
    zoomOut,
    resetZoom,
    seekTo,
    isReady,
    isPlaying,
    currentTime,
    duration,
    zoomLevel,
    formatTime,
    loopRegion,
    isLooping,
    setLoopRegion,
    toggleLoop,
    clearLoopRegion,
    interactionMode,
    setInteractionMode,
    toggleInteractionMode,
  }), [play, pause, stop, zoomIn, zoomOut, resetZoom, seekTo, isReady, isPlaying, currentTime, duration, zoomLevel, formatTime, loopRegion, isLooping, setLoopRegion, toggleLoop, clearLoopRegion, interactionMode, setInteractionMode, toggleInteractionMode]);

  // Notify parent when controls are ready
  useEffect(() => {
    onControlsReady?.({
      play,
      pause,
      stop,
      zoomIn,
      zoomOut,
      resetZoom,
      seekTo,
      isReady,
      isPlaying,
      currentTime,
      duration,
      zoomLevel,
      formatTime,
      loopRegion,
      isLooping,
      setLoopRegion,
      toggleLoop,
      clearLoopRegion,
      interactionMode,
      setInteractionMode,
      toggleInteractionMode,
    });
  }, [onControlsReady, play, pause, stop, zoomIn, zoomOut, resetZoom, seekTo, isReady, isPlaying, duration, zoomLevel, formatTime, loopRegion, isLooping, setLoopRegion, toggleLoop, clearLoopRegion, interactionMode, setInteractionMode, toggleInteractionMode]);

  // Notify parent of playback state changes
  useEffect(() => {
    onPlaybackStateChange?.({
      isPlaying,
      currentTime,
      duration,
    });
  }, [onPlaybackStateChange, isPlaying, currentTime, duration]);

  // Stop the inactive waveform when play mode changes (opacity changes)
  useEffect(() => {
    const isAccelerationActive = accelerationOpacity >= velocityOpacity;
    const inactiveWs = isAccelerationActive
      ? velocityWsRef.current
      : accelerationWsRef.current;

    // Stop and reset the inactive waveform
    if (inactiveWs) {
      inactiveWs.pause();
      inactiveWs.setTime(0);
    }
  }, [accelerationOpacity, velocityOpacity]);

  // Helper to create WaveSurfer instance
  const createWaveSurfer = useCallback((
    container: HTMLDivElement,
    data: number[],
    color: string,
    sampleRate: number,
    chartHeight: number,
    showCursor: boolean
  ): WaveSurfer => {
    // Clear container first
    container.innerHTML = '';

    const ws = WaveSurfer.create({
      container,
      height: chartHeight,
      waveColor: color,
      progressColor: color,
      cursorColor: showCursor ? colors.cursor : 'transparent',
      cursorWidth: showCursor ? 2 : 0,
      barWidth: 1,
      barGap: 0,
      barRadius: 0,
      normalize: true,
      // Use a small initial value - actual zoom is applied after ready (auto-fit)
      minPxPerSec: MIN_ZOOM,
      fillParent: true,
      autoScroll: true,
      autoCenter: true,
      backend: 'WebAudio',
    });

    // Make WaveSurfer's internal elements transparent for overlay effect
    // WaveSurfer v7+ renders to an SVG element - ensure all backgrounds are transparent
    ws.on('ready', () => {
      // Set all internal elements to transparent
      const allElements = container.querySelectorAll('*');
      allElements.forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.background = 'transparent';
          el.style.backgroundColor = 'transparent';
        }
      });
      // Also SVG elements
      const svgElements = container.querySelectorAll('svg');
      svgElements.forEach((svg) => {
        svg.style.background = 'transparent';
        svg.style.backgroundColor = 'transparent';
      });
      container.style.background = 'transparent';
      container.style.backgroundColor = 'transparent';
    });

    // Load waveform data
    if (data && data.length > 0) {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = audioContext.createBuffer(1, data.length, sampleRate);
      const channelData = audioBuffer.getChannelData(0);

      // Normalize data
      let maxAbs = 0;
      for (let i = 0; i < data.length; i++) {
        const absVal = Math.abs(data[i]);
        if (absVal > maxAbs) maxAbs = absVal;
      }
      maxAbs = maxAbs || 1;

      for (let i = 0; i < data.length; i++) {
        channelData[i] = data[i] / maxAbs;
      }

      // Convert to WAV and load
      const wavBlob = new Blob([audioBufferToWav(audioBuffer)], { type: 'audio/wav' });
      ws.loadBlob(wavBlob);

      audioContext.close();
    }

    return ws;
  }, [colors.cursor]);

  // Initialize WaveSurfer instances
  useEffect(() => {
    // Clean up previous instances
    if (accelerationWsRef.current) {
      accelerationWsRef.current.destroy();
      accelerationWsRef.current = null;
    }
    if (velocityWsRef.current) {
      velocityWsRef.current.destroy();
      velocityWsRef.current = null;
    }
    setIsReady(false);
    setIsPlaying(false);
    // Clear loop region when traces change (e.g., switching between 5s/30s modes)
    setLoopRegionState(null);
    setIsLooping(false);
    // Clear auto-fit zoom so it's recalculated for new data
    setAutoFitZoom(null);

    let readyCount = 0;
    const expectedCount = (accelerationTrace ? 1 : 0) + (velocityTrace ? 1 : 0);

    if (expectedCount === 0) return;

    const checkAllReady = () => {
      readyCount++;
      if (readyCount >= expectedCount) {
        setIsReady(true);
        // Get duration from first available trace
        const ws = accelerationWsRef.current || velocityWsRef.current;
        if (ws) {
          const wsDuration = ws.getDuration();
          setDuration(wsDuration);

          // Calculate and apply auto-fit zoom (like Plotly behavior)
          // This ensures the entire waveform fits in the container without scrolling
          if (chartContainerRef.current) {
            const containerWidth = chartContainerRef.current.clientWidth;
            const fitZoom = containerWidth / wsDuration;
            const clampedZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitZoom));

            setAutoFitZoom(clampedZoom);
            setZoomLevel(clampedZoom);

            // Apply zoom to WaveSurfer instances
            accelerationWsRef.current?.zoom(clampedZoom);
            velocityWsRef.current?.zoom(clampedZoom);
          }
        }
      }
    };

    // Create acceleration waveform - always show cursor
    if (accelerationContainerRef.current && accelerationTrace && accelerationTrace.data?.length > 0) {
      const ws = createWaveSurfer(
        accelerationContainerRef.current,
        accelerationTrace.data,
        accelerationTrace.color,
        accelerationTrace.sampleRate || 44100,
        waveformHeight,
        true // Show cursor
      );
      accelerationWsRef.current = ws;

      ws.on('ready', checkAllReady);
      // Both waveforms update time - since only one plays at a time (inactive is stopped),
      // only the playing waveform will trigger these events
      ws.on('audioprocess', (time) => {
        setCurrentTime(time);
        onTimeUpdate?.(time);
      });
      ws.on('seeking', (time) => {
        setCurrentTime(time);
      });
      ws.on('play', () => setIsPlaying(true));
      ws.on('pause', () => setIsPlaying(false));
      ws.on('finish', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
    }

    // Create velocity waveform - always show cursor
    if (velocityContainerRef.current && velocityTrace && velocityTrace.data?.length > 0) {
      const ws = createWaveSurfer(
        velocityContainerRef.current,
        velocityTrace.data,
        velocityTrace.color,
        velocityTrace.sampleRate || 44100,
        waveformHeight,
        true // Show cursor on velocity too
      );
      velocityWsRef.current = ws;

      ws.on('ready', checkAllReady);
      // Both waveforms update time - since only one plays at a time (inactive is stopped),
      // only the playing waveform will trigger these events
      ws.on('audioprocess', (time) => {
        setCurrentTime(time);
        onTimeUpdate?.(time);
      });
      ws.on('seeking', (time) => {
        setCurrentTime(time);
      });
      ws.on('play', () => setIsPlaying(true));
      ws.on('pause', () => setIsPlaying(false));
      ws.on('finish', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
    }

    return () => {
      accelerationWsRef.current?.destroy();
      velocityWsRef.current?.destroy();
      accelerationWsRef.current = null;
      velocityWsRef.current = null;
      setIsReady(false);
    };
  }, [accelerationTrace?.id, accelerationTrace?.data?.length, velocityTrace?.id, velocityTrace?.data?.length, waveformHeight, createWaveSurfer, onTimeUpdate]);

  // If no traces, show empty state - matching PlotlyChart empty state styling
  if ((!accelerationTrace && !velocityTrace) || traces.every(t => !t.data || t.data.length === 0)) {
    return (
      <div
        className={cn(
          'flex items-center justify-center min-h-[300px] bg-white dark:bg-[#52606d] rounded',
          className
        )}
        style={{ height }}
      >
        <div className="flex flex-col items-center justify-center text-[#55687d] dark:text-[#8b98a5]">
          <p className="text-sm">No waveform data available</p>
        </div>
      </div>
    );
  }

  // Check if any selection mode is active
  const isAnySelectionMode = isMarkerSelectionMode || isHarmonicSelectionMode || isSideBandsSelectionMode;

  return (
    <div className={cn('relative w-full', className)} style={{ height }}>
      {/* Pending harmonic indicator - show the first click position */}
      {pendingHarmonicCreation && (
        <div
          className="absolute top-6 z-40 w-0.5 h-full pointer-events-none"
          style={{
            left: `calc(${(pendingHarmonicCreation.centerPoint / (duration > 0 ? duration : 5)) * 100}% + 48px)`,
            backgroundColor: pendingHarmonicCreation.color,
            opacity: 0.7,
          }}
        />
      )}

      {/* Waveform chart area */}
      <div className="relative flex flex-col h-full">
        {/* Recording timestamp indicator (shows when actual differs from requested) */}
        {timestampInfo && (
          <div className="flex items-center justify-end gap-2 px-2 py-1 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Recording:</span>
              <span className="font-mono font-medium text-foreground">{timestampInfo.formattedActual}</span>
              {timestampInfo.isDifferent && (
                <span className="text-amber-500 dark:text-amber-400 text-[10px]">
                  (nearest to {timestampInfo.formattedRequested})
                </span>
              )}
            </div>
          </div>
        )}

        {/* Y-axis labels + overlaid waveforms */}
        <div className="flex flex-1">
          {/* Y-axis */}
          <div className="w-12 flex flex-col justify-between py-2 text-right pr-2" style={{ height: waveformHeight }}>
            <span className="text-xs text-muted-foreground">1</span>
            <span className="text-xs text-muted-foreground">0</span>
            <span className="text-xs text-muted-foreground">-1</span>
          </div>

          {/* Overlaid waveform container */}
          <div
            ref={chartContainerRef}
            className={cn(
              "flex-1 relative select-none",
              isAnySelectionMode && "cursor-crosshair",
              !isAnySelectionMode && interactionMode === 'zoom' && "cursor-zoom-in",
              !isAnySelectionMode && interactionMode === 'handlebars' && "cursor-col-resize"
            )}
            style={{
              height: waveformHeight,
              backgroundColor: colors.background
            }}
            onMouseDown={(e) => {
              // Don't start region creation in selection modes
              if (isAnySelectionMode) return;

              const time = getTimeFromMouseEvent(e);
              setRegionStartTime(time);
              setIsCreatingRegion(true);

              if (interactionMode === 'zoom') {
                // Zoom mode: start zoom selection
                setZoomSelectionStart(time);
                setZoomSelectionEnd(time);
              } else {
                // Handlebars mode: clear existing loop and start new region
                setLoopRegionState(null);
                setIsLooping(false);
              }
            }}
            onClick={(e) => {
              // Don't process click if we were dragging
              if (isCreatingRegion || isDraggingHandle) return;

              // Calculate time from click position
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const percentage = x / rect.width;
              const effectiveDuration = duration > 0 ? duration : 5;
              const time = percentage * effectiveDuration;

              // Handle different selection modes
              if (isHarmonicSelectionMode || isSideBandsSelectionMode) {
                // Harmonic or sideband mode - call the harmonic click handler
                onHarmonicClick?.(time);
                return;
              }

              if (isMarkerSelectionMode && onAddMarker) {
                // Regular marker mode
                onAddMarker(time);
                return;
              }
            }}
          >
            {/* Both waveforms overlaid - velocity first (bottom layer), then acceleration (top) */}
            {/* Velocity waveform */}
            {velocityTrace && (
              <div
                ref={velocityContainerRef}
                className="absolute inset-0 pointer-events-none"
                style={{
                  opacity: velocityOpacity,
                  transition: 'opacity 0.3s ease',
                  zIndex: 1,
                  background: 'transparent',
                }}
              />
            )}

            {/* Acceleration waveform (on top, receives pointer events) */}
            {accelerationTrace && (
              <div
                ref={accelerationContainerRef}
                className="absolute inset-0 pointer-events-none"
                style={{
                  opacity: accelerationOpacity,
                  transition: 'opacity 0.3s ease',
                  zIndex: 2,
                  background: 'transparent',
                }}
              />
            )}

            {/* Loop region overlay with draggable handles */}
            {loopRegion && duration > 0 && (
              <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 50 }}>
                {/* Highlighted loop region */}
                <div
                  className="absolute top-0 bottom-0 pointer-events-auto cursor-pointer"
                  style={{
                    left: `${(loopRegion.start / duration) * 100}%`,
                    width: `${((loopRegion.end - loopRegion.start) / duration) * 100}%`,
                    backgroundColor: isLooping ? 'rgba(34, 197, 94, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                    borderLeft: `2px solid ${isLooping ? '#22c55e' : '#3b82f6'}`,
                    borderRight: `2px solid ${isLooping ? '#22c55e' : '#3b82f6'}`,
                  }}
                  onMouseDown={(e) => {
                    // Stop mousedown from bubbling to container (which clears the region)
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    // Toggle loop on click
                    setIsLooping(prev => !prev);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    // Clear region on double-click
                    clearLoopRegion();
                  }}
                >
                  {/* Start time label */}
                  <div
                    className="absolute -top-5 left-0 -translate-x-1/2 text-[10px] font-mono px-1 rounded pointer-events-none"
                    style={{
                      backgroundColor: isLooping ? '#22c55e' : '#3b82f6',
                      color: 'white',
                    }}
                  >
                    {formatTime(loopRegion.start)}
                  </div>
                  {/* End time label */}
                  <div
                    className="absolute -top-5 right-0 translate-x-1/2 text-[10px] font-mono px-1 rounded pointer-events-none"
                    style={{
                      backgroundColor: isLooping ? '#22c55e' : '#3b82f6',
                      color: 'white',
                    }}
                  >
                    {formatTime(loopRegion.end)}
                  </div>
                  {/* Loop indicator in center - clickable to toggle loop */}
                  <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1 cursor-pointer"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsLooping(prev => !prev);
                    }}
                  >
                    <div
                      className="text-[10px] font-medium px-1.5 py-0.5 rounded hover:opacity-80 transition-opacity"
                      style={{
                        backgroundColor: isLooping ? '#22c55e' : '#3b82f6',
                        color: 'white',
                      }}
                    >
                      {isLooping ? 'LOOP ON' : 'Click to loop'}
                    </div>
                  </div>
                  {/* Start handle - prominent vertical bar with grip lines */}
                  <div
                    className="absolute left-0 top-0 bottom-0 w-4 cursor-ew-resize group"
                    style={{ transform: 'translateX(-50%)' }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setIsDraggingHandle('start');
                    }}
                  >
                    {/* Handle bar */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 top-2 bottom-2 w-3 rounded-sm shadow-md transition-all group-hover:w-4 group-hover:shadow-lg flex items-center justify-center"
                      style={{
                        backgroundColor: isLooping ? '#22c55e' : '#3b82f6',
                      }}
                    >
                      {/* Grip lines */}
                      <div className="flex flex-col gap-1">
                        <div className="w-1.5 h-0.5 bg-white/60 rounded-full" />
                        <div className="w-1.5 h-0.5 bg-white/60 rounded-full" />
                        <div className="w-1.5 h-0.5 bg-white/60 rounded-full" />
                      </div>
                    </div>
                  </div>
                  {/* End handle - prominent vertical bar with grip lines */}
                  <div
                    className="absolute right-0 top-0 bottom-0 w-4 cursor-ew-resize group"
                    style={{ transform: 'translateX(50%)' }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setIsDraggingHandle('end');
                    }}
                  >
                    {/* Handle bar */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 top-2 bottom-2 w-3 rounded-sm shadow-md transition-all group-hover:w-4 group-hover:shadow-lg flex items-center justify-center"
                      style={{
                        backgroundColor: isLooping ? '#22c55e' : '#3b82f6',
                      }}
                    >
                      {/* Grip lines */}
                      <div className="flex flex-col gap-1">
                        <div className="w-1.5 h-0.5 bg-white/60 rounded-full" />
                        <div className="w-1.5 h-0.5 bg-white/60 rounded-full" />
                        <div className="w-1.5 h-0.5 bg-white/60 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Zoom selection overlay (shown while dragging in zoom mode) */}
            {interactionMode === 'zoom' && zoomSelectionStart !== null && zoomSelectionEnd !== null && duration > 0 && (
              <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 45 }}>
                <div
                  className="absolute top-0 bottom-0"
                  style={{
                    left: `${(Math.min(zoomSelectionStart, zoomSelectionEnd) / duration) * 100}%`,
                    width: `${(Math.abs(zoomSelectionEnd - zoomSelectionStart) / duration) * 100}%`,
                    backgroundColor: 'rgba(139, 92, 246, 0.2)', // Purple overlay for zoom
                    borderLeft: '2px solid #8b5cf6',
                    borderRight: '2px solid #8b5cf6',
                  }}
                >
                  {/* Zoom label */}
                  <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[10px] font-medium px-2 py-1 rounded bg-violet-500 text-white whitespace-nowrap"
                  >
                    Zoom to region
                  </div>
                </div>
              </div>
            )}

            {/* Marker overlay layer */}
            <div className="absolute inset-0" style={{ zIndex: 100, pointerEvents: 'none' }}>
              {markers.map((marker) => {
                const percentage = duration > 0 ? (marker.time / duration) * 100 : 0;
                const isSelected = selectedMarkerId === marker.id;
                const markerColor = marker.color || '#000000'; // Black default

                // Line style - solid or dashed (thin lines)
                const lineStyle: React.CSSProperties = marker.isDashed
                  ? {
                      width: '1px',
                      backgroundImage: `repeating-linear-gradient(to bottom, ${markerColor} 0px, ${markerColor} 4px, transparent 4px, transparent 8px)`,
                    }
                  : {
                      width: '1px',
                      backgroundColor: markerColor,
                    };

                return (
                  <div
                    key={marker.id}
                    className="absolute top-0 bottom-0 cursor-pointer group"
                    style={{
                      left: `${percentage}%`,
                      transform: 'translateX(-50%)',
                      pointerEvents: 'auto',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkerClick?.(marker.id, { x: e.clientX, y: e.clientY });
                    }}
                  >
                    {/* Marker line - solid or dashed */}
                    <div
                      className="h-full transition-all"
                      style={lineStyle}
                    />
                    {/* Marker label */}
                    <div
                      className="absolute top-4 left-1/2 -translate-x-1/2 px-1.5 py-0.5 rounded text-xs font-medium whitespace-nowrap bg-background shadow-sm"
                      style={{ color: markerColor }}
                    >
                      {marker.label || `${marker.time.toFixed(2)}s`}
                    </div>
                    {/* Hover/click area - wider for easier clicking */}
                    <div className="absolute inset-y-0 -left-4 -right-4" />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* X-axis time labels */}
        <div className="h-8 flex items-center justify-between px-4 text-xs text-muted-foreground ml-12">
          <span>0:00</span>
          <span>{formatTime(duration / 4)}</span>
          <span>{formatTime(duration / 2)}</span>
          <span>{formatTime((duration * 3) / 4)}</span>
          <span>{formatTime(duration)}</span>
        </div>

        {/* Legend at bottom - matching PlotlyChart legend style */}
        <div className="h-8 flex items-center justify-center gap-6">
          {accelerationTrace && (
            <div className="flex items-center gap-1.5">
              <div
                className="w-3 h-0.5"
                style={{ backgroundColor: accelerationTrace.color }}
              />
              <span className="text-[11px] text-muted-foreground">Acceleration</span>
            </div>
          )}
          {velocityTrace && (
            <div className="flex items-center gap-1.5">
              <div
                className="w-3 h-0.5"
                style={{ backgroundColor: velocityTrace.color }}
              />
              <span className="text-[11px] text-muted-foreground">Velocity</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

MultiWaveSurferChart.displayName = 'MultiWaveSurferChart';

/**
 * Convert AudioBuffer to WAV format
 */
function audioBufferToWav(buffer: AudioBuffer): ArrayBuffer {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1;
  const bitDepth = 16;

  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = buffer.length * blockAlign;
  const headerSize = 44;
  const totalSize = headerSize + dataSize;

  const arrayBuffer = new ArrayBuffer(totalSize);
  const view = new DataView(arrayBuffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, totalSize - 8, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  const channelData = buffer.getChannelData(0);
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    const sample = Math.max(-1, Math.min(1, channelData[i]));
    const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
    view.setInt16(offset, intSample, true);
    offset += bytesPerSample;
  }

  return arrayBuffer;
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

export default MultiWaveSurferChart;
